import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ChevronLeft, User, Heart, Bell, LogOut, 
  ChevronRight, Share2, Target, DollarSign, Sun
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: user?.name || '',
    faith_mode: user?.faith_mode || 'christian',
    tone: user?.tone || 'gentle',
    session_length: user?.session_length || 'short',
    notification_time: user?.notification_time || '08:00'
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/users/profile`, settings);
      updateUser(response.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('See you soon! 🙏');
  };

  const settingSections = [
    {
      title: 'Profile',
      items: [
        {
          icon: User,
          label: 'Name',
          value: settings.name,
          type: 'input',
          onChange: (value) => setSettings(prev => ({ ...prev, name: value }))
        }
      ]
    },
    {
      title: 'Faith Preferences',
      items: [
        {
          icon: Heart,
          label: 'Faith Mode',
          value: settings.faith_mode,
          type: 'select',
          options: [
            { value: 'christian', label: 'Christian' },
            { value: 'general', label: 'General Faith' }
          ],
          onChange: (value) => setSettings(prev => ({ ...prev, faith_mode: value }))
        },
        {
          icon: Sun,
          label: 'Tone',
          value: settings.tone,
          type: 'select',
          options: [
            { value: 'gentle', label: 'Gentle & Warm' },
            { value: 'direct', label: 'Direct & Clear' }
          ],
          onChange: (value) => setSettings(prev => ({ ...prev, tone: value }))
        },
        {
          icon: Target,
          label: 'Session Length',
          value: settings.session_length,
          type: 'select',
          options: [
            { value: 'short', label: '3-5 minutes' },
            { value: 'medium', label: '8-10 minutes' },
            { value: 'long', label: '15 minutes' }
          ],
          onChange: (value) => setSettings(prev => ({ ...prev, session_length: value }))
        }
      ]
    },
    {
      title: 'Notifications',
      items: [
        {
          icon: Bell,
          label: 'Daily Reminder',
          value: settings.notification_time,
          type: 'time',
          onChange: (value) => setSettings(prev => ({ ...prev, notification_time: value }))
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="rounded-full"
          data-testid="back-button"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-serif">Settings</h1>
      </header>

      <main className="px-6 space-y-6 pb-8">
        {settingSections.map((section, sIdx) => (
          <div key={sIdx}>
            <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">
              {section.title}
            </h2>
            <Card className="rounded-2xl border-border/40">
              <CardContent className="p-0 divide-y divide-border/40">
                {section.items.map((item, iIdx) => {
                  const Icon = item.icon;
                  return (
                    <div key={iIdx} className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{item.label}</p>
                        {item.type === 'input' && (
                          <Input
                            value={item.value}
                            onChange={(e) => item.onChange(e.target.value)}
                            className="mt-1 h-10 rounded-xl bg-muted/50 border-transparent"
                            data-testid={`setting-${item.label.toLowerCase()}`}
                          />
                        )}
                        {item.type === 'select' && (
                          <select
                            value={item.value}
                            onChange={(e) => item.onChange(e.target.value)}
                            className="mt-1 w-full h-10 rounded-xl bg-muted/50 border-transparent px-3 text-sm"
                            data-testid={`setting-${item.label.toLowerCase()}`}
                          >
                            {item.options.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        )}
                        {item.type === 'time' && (
                          <Input
                            type="time"
                            value={item.value}
                            onChange={(e) => item.onChange(e.target.value)}
                            className="mt-1 h-10 rounded-xl bg-muted/50 border-transparent w-auto"
                            data-testid={`setting-${item.label.toLowerCase()}`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        ))}

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-12 rounded-full"
          data-testid="save-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>

        {/* Quick Links */}
        <Card className="rounded-2xl border-border/40">
          <CardContent className="p-0 divide-y divide-border/40">
            <Link to="/donate" className="p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors" data-testid="donate-link">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Support BlessBe</p>
                <p className="text-sm text-muted-foreground">Donate a dollar to help us grow</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
            <Link to="/invite" className="p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Share2 className="w-5 h-5 text-accent" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Invite Someone You Love</p>
                <p className="text-sm text-muted-foreground">Share the blessing</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>

        {/* Logout */}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full h-12 rounded-full text-destructive hover:bg-destructive/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          BlessBe v1.0 • Made with love
        </p>
      </main>
    </div>
  );
}
